import { createGlobalStyle } from 'styled-components';

const primitives = {
  0: '0rem', // 0px
  0.5: '0.125rem', // 2px
  1: '0.25rem', // 4px
  1.5: '0.375rem', // 6px
  2: '0.5rem', // 8px
  2.5: '0.625rem', // 10px
  3: '0.75rem', // 12px
  4: '1rem', // 16px
  5: '1.25rem', // 20px
  6: '1.5rem', // 24px
  8: '2rem', // 32px
  10: '2.5rem', // 40px
  12: '3rem', // 48px
  16: '4rem', // 64px
  20: '5rem', // 80px
  24: '6rem', // 96px
  32: '8rem', // 128px
  40: '10rem', // 160px
  48: '12rem', // 192px
  56: '14rem', // 224px
  64: '16rem', // 256px
  80: '20rem', // 320px
  96: '24rem', // 384px
  120: '30rem', // 480px
  140: '35rem', // 560px
  160: '40rem', // 640px
  180: '45rem', // 720px
  192: '48rem', // 768px
  256: '64rem', // 1024px
  320: '80rem', // 1280px
  360: '90rem', // 1440px
  400: '100rem', // 1600px
  480: '120rem', // 1920px
};

const spacing = {
  auto: 'auto',
  none: primitives[0],
  xxs: primitives[0.5],
  xs: primitives[1],
  sm: primitives[1.5],
  md: primitives[2],
  lg: primitives[3],
  xl: primitives[4],
  '2xl': primitives[5],
  '3xl': primitives[6],
  '4xl': primitives[8],
  '5xl': primitives[10],
  '6xl': primitives[12],
  '7xl': primitives[16],
  '8xl': primitives[20],
  '9xl': primitives[24],
  '10xl': primitives[32],
  '11xl': primitives[40],
};
Object.freeze(spacing);

const width = {
  'fit-content': 'fit-content',
  xxs: primitives[80],
  xs: primitives[96],
  sm: primitives[120],
  md: primitives[140],
  lg: primitives[160],
  xl: primitives[192],
  '2xl': primitives[256],
  '3xl': primitives[320],
  '4xl': primitives[360],
  '5xl': primitives[400],
  '6xl': primitives[480],
};
Object.freeze(width);

const radius = {
  none: primitives[0],
  xxs: primitives[0.5],
  xs: primitives[1],
  sm: primitives[1.5],
  md: primitives[2],
  lg: primitives[2.5],
  xl: primitives[3],
  '2xl': primitives[4],
  '3xl': primitives[5],
  '4xl': primitives[6],
  full: primitives[480],
  circle: '50%',
};
Object.freeze(radius);

const variables = { spacing, width, radius };
const spacingGlobalVariables = Object.keys(variables)
  .map((key) => {
    if (key === 'spacing') {
      return Object.keys(variables[key]).map((subKey) => {
        return `--${key}-${subKey}: ${variables[key][subKey as keyof typeof spacing]};`;
      });
    }
    if (key === 'width') {
      return Object.keys(variables[key]).map((subKey) => {
        return `--${key}-${subKey}: ${variables[key][subKey as keyof typeof width]};`;
      });
    }
    if (key === 'radius') {
      return Object.keys(variables[key]).map((subKey) => {
        return `--${key}-${subKey}: ${variables[key][subKey as keyof typeof radius]};`;
      });
    }
  })
  .flat();

export type SpacingPropKeys = `spacing-${keyof typeof spacing}`;
export type WidthPropKeys = `width-${keyof typeof width}`;
export type RadiusPropKeys = `radius-${keyof typeof radius}`;

export type SpacingAndSizePropKeys = SpacingPropKeys | WidthPropKeys | RadiusPropKeys;

export { spacing, width, radius };

export const SpacingGlobalVariables = createGlobalStyle`
  :root {
    ${spacingGlobalVariables.join('\n')}
  }
`;
